'use client'

import { useKnockClient, useNotifications, useNotificationStore } from '@knocklabs/react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { FeedClientOptions } from '@knocklabs/client'
import { displayFeature, FEATURE_FLAGS } from '@scalis/feature-flags'
import { Routes } from '~/packages/libs/routes/src'
import { ButtonSize, Drawer, Button, ButtonStandaloneKind, ButtonIcon, EmptyState } from '~/scalis-components/core'
import { SegmentedControl } from '~/scalis-components/core/segmented-control'
import { useKnockProviderContext } from '~/src/providers/knock-provider-wrapper/knock-provider-context'
import NavbarNotificationBell from '../navbar-notification-bell/navbar-notification-bell'
import NavbarNotificationToaster from '../navbar-notification-toaster/navbar-notification-toaster'
import NavbarNotificationsDrawerItem from '../navbar-notifications-drawer-item/navbar-notifications-drawer-item'

const NavbarNotificationsDrawerContent = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const isInitialFetchRef = useRef(true)

  const [notificationsStatus, setNotificationsStatus] = useState<FeedClientOptions['status']>('all')

  // Create a stable reference to knockClient that won't change between renders
  const knockClient = useKnockClient()

  // Call hooks at the top level, not inside callbacks
  const feedClient = useNotifications(knockClient, process.env.NEXT_PUBLIC_KNOCK_FEED_ID ?? '', {
    page_size: 500,
  })

  const { items, metadata } = useNotificationStore(feedClient)
  const unreadCount = metadata?.unread_count

  useEffect(() => {
    // Skip duplicate fetches on initial render
    if (isInitialFetchRef.current) {
      isInitialFetchRef.current = false
      return
    }
    feedClient.fetch({ status: notificationsStatus })
  }, [feedClient, notificationsStatus, knockClient])

  // Knock has a filtering bug, basically whenever user doesn't has any unread notifications, but then receives one, Knock returns all notifications, even though "status" is "unread"
  const filteredItems = useMemo(() => {
    if (notificationsStatus === 'all') {
      return items
    }
    return items.filter((item) => item.read_at === null)
  }, [items, notificationsStatus])

  return (
    <>
      <NavbarNotificationBell
        unreadCount={unreadCount ?? 0}
        onClick={() => setIsDrawerOpen(true)}
      />
      <Drawer
        title='Notifications'
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        roundedEdges
        hideFooterContent
        contentClassName='p-0 flex flex-col h-full'
        actions={
          displayFeature(FEATURE_FLAGS.NOTIFICATION_PREFERENCES) ? (
            <Link href={Routes.company.settings.notifications}>
              <ButtonIcon
                icon='fa-regular fa-gear'
                size='small'
                data-testid='notification-preferences-gear-icon'
              />
            </Link>
          ) : null
        }
      >
        <div className='p-4 flex justify-between items-center border-b border-borders'>
          <SegmentedControl
            tabs={[
              {
                id: 'all',
                label: 'All',
              },
              {
                id: 'unread',
                label: 'Unread',
                badgeValue: unreadCount > 0 ? unreadCount : undefined,
              },
            ]}
            activeTab={notificationsStatus ?? 'all'}
            onTabChange={(tab) => setNotificationsStatus(tab as FeedClientOptions['status'])}
          />
          <Button
            isStandalone
            size={ButtonSize.medium}
            kind={ButtonStandaloneKind.brand}
            endAdornment='fa-regular fa-check-double'
            type='button'
            disabled={unreadCount === 0}
            onClick={async () => {
              await feedClient.markAllAsRead()
              await feedClient.fetch({ status: notificationsStatus })
            }}
            data-testid='mark-all-as-read-button'
            className='cursor-pointer'
          >
            Mark all as read
          </Button>
        </div>
        <div className='flex-1 overflow-y-auto'>
          {filteredItems.length > 0 ? (
            filteredItems.map((item, index) => (
              <NavbarNotificationsDrawerItem
                key={item.id}
                item={item}
                hasBottomBorder={index !== filteredItems.length - 1}
                onMarkAsReadClick={() => {
                  feedClient.markAsRead(item)
                }}
                onMarkAsUnreadClick={() => {
                  feedClient.markAsUnread(item)
                }}
              />
            ))
          ) : (
            <EmptyState
              title='No Notifications Yet!'
              message='There are no notifications at this time. You can find updates, messages and important alerts right here.'
              icon='fa-regular fa-bell'
              className='self-center'
            />
          )}
        </div>
      </Drawer>
      <NavbarNotificationToaster
        feedClient={feedClient}
        feedItems={filteredItems}
      />
    </>
  )
}

const NavbarNotificationsDrawer = () => {
  const { isKnockInitialized } = useKnockProviderContext()

  if (!isKnockInitialized) {
    return null
  }

  return <NavbarNotificationsDrawerContent />
}

export default NavbarNotificationsDrawer
